import { PROTOCOL_DELIVERY_STATES, PROTOCOL_KINDS, REMARKS_STATUSES, type TaskFieldDto } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Group, Modal, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { IconFileTypePdf } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { can as canDo, useMe } from '../../auth';
import { formatDateTime, plural } from '../../format';
import { minHint } from '../../minHint';
import { notifyError, notifyOk } from '../../notify';
import { ProtocolSigningPanel } from './ProtocolSigningPanel';

const MAIL_STATUS: Record<string, { label: string; color: string }> = {
  oczekuje: { label: 'w kolejce', color: 'gray' },
  wysylanie: { label: 'wysyłanie', color: 'blue' },
  wyslany: { label: 'wysłano (przyjęte przez serwer poczty)', color: 'green' },
  blad: { label: 'błąd — ponowna próba', color: 'orange' },
  blad_trwaly: { label: 'błąd — wymaga działania', color: 'red' },
  do_sprawdzenia: { label: 'do sprawdzenia', color: 'orange' },
};

export const deliveryColor = (d: string) => (d === 'wyslano' ? 'green' : d === 'bez_wysylki' ? 'yellow' : d.startsWith('blad') || d === 'odrzucony' ? 'red' : d === 'do_sprawdzenia' ? 'orange' : 'gray');

type P = TaskFieldDto['protocols'][number];

/** Protokoły zadania w biurze: stan PDF i wysyłki, historia wiadomości, ponowna wysyłka i unieważnienie (z powodem). */
export function ProtocolsBlock({ taskId, protocols, can }: { taskId: string; protocols: P[]; can: TaskFieldDto['can'] }) {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<{ kind: 'void' | 'resend' | 'rework' | 'resolve'; p: P } | null>(null);
  const me = useMe().data;
  const planner = canDo(me, 'planowanie.edycja');
  const [reason, setReason] = useState('');
  const [email, setEmail] = useState('');
  const run = useMutation({
    mutationFn: () =>
      dialog!.kind === 'void'
        ? api(`/protocols/${dialog!.p.id}/void`, { method: 'POST', body: { reason } })
        : dialog!.kind === 'rework'
          ? api(`/protocols/${dialog!.p.id}/rework`, { method: 'POST', body: { description: reason } })
          : dialog!.kind === 'resolve'
            ? api(`/protocols/${dialog!.p.id}/remarks/resolve`, { method: 'POST', body: { comment: reason } })
            : api(`/protocols/${dialog!.p.id}/resend`, { method: 'POST', body: { email, reason } }),
    onSuccess: () => {
      const k = dialog!.kind;
      notifyOk(k === 'void' ? 'Protokół unieważniony' : k === 'rework' ? 'Utworzono zadanie „Poprawka” — zaplanuj je w Planowaniu' : k === 'resolve' ? 'Uwagi oznaczone jako rozpatrzone' : 'Wiadomość dodana do kolejki wysyłki');
      setDialog(null);
      void qc.invalidateQueries({ queryKey: ['task-field', taskId] });
      void qc.invalidateQueries({ queryKey: ['case-tasks'] });
      void qc.invalidateQueries({ queryKey: ['case-process'] });
    },
    onError: notifyError,
  });
  // unieważnienie: serwer wymaga co najmniej 5 znaków powodu (protocolVoidSchema), pozostałe — 3
  const minReason = dialog?.kind === 'void' ? 5 : 3;
  // minHint odmienia tylko licznik brakujących znaków — pełna podpowiedź z poprawną odmianą minimum
  const reasonHint = reason.trim() && reason.trim().length < minReason ? minHint(reason, minReason) : `Wymagane — co najmniej ${minReason} ${plural(minReason, 'znak', 'znaki', 'znaków')}; zapisze się w historii`;
  if (!protocols.length) return null;
  return (
    <Stack gap={6}>
      <Text size="sm" fw={600}>
        Protokoły
      </Text>
      {protocols.map((p) => (
        <Alert key={p.id} color={p.status === 'uniewazniony' ? 'gray' : p.status === 'do_podpisu' ? 'orange' : deliveryColor(p.delivery)} variant="light" p="xs">
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <div>
              <Text size="sm" fw={600} td={p.status === 'uniewazniony' ? 'line-through' : undefined}>
                {p.code} — {PROTOCOL_KINDS[p.kind]}
              </Text>
              {p.signing ? (
                <ProtocolSigningPanel
                  protocolId={p.id}
                  code={p.code}
                  email={p.email}
                  signerName={p.signerName}
                  s={p.signing}
                  onChanged={() => void qc.invalidateQueries({ queryKey: ['task-field', taskId] })}
                />
              ) : (
                <Text size="xs">
                  Podpisał: {p.signerName}, {formatDateTime(p.signedAt)}
                </Text>
              )}
              {p.incompleteChecklist?.length ? (
                <Stack gap={2} mt={4}>
                  <Badge size="sm" color="red">
                    Podpisany przy niepełnej checkliście
                  </Badge>
                  <Text size="xs">Brakowało: {p.incompleteChecklist.join(', ')}. Uzupełnienia nie trafiły do protokołu klienta — w razie potrzeby unieważnij go i zleć nowy.</Text>
                </Stack>
              ) : null}
              {p.remarksStatus && p.remarksStatus !== 'brak' && p.status === 'podpisany' && (
                <Stack gap={2} mt={4}>
                  <Badge size="sm" color={p.remarksStatus === 'rozpatrzone' ? 'green' : p.remarksStatus === 'poprawka' ? 'blue' : 'orange'}>
                    {REMARKS_STATUSES[p.remarksStatus]}
                  </Badge>
                  <Text size="xs">Uwagi klienta: „{p.clientRemarks}”</Text>
                  {p.remarksResolution && (
                    <Text size="xs" c="dimmed">
                      {p.remarksResolution}
                    </Text>
                  )}
                  {planner && (p.remarksStatus === 'uwagi' || p.remarksStatus === 'poprawka') && (
                    <Group gap={4}>
                      <Button size="compact-xs" onClick={() => (setDialog({ kind: 'rework', p }), setReason(''))}>
                        Zleć poprawkę
                      </Button>
                      <Button size="compact-xs" variant="subtle" onClick={() => (setDialog({ kind: 'resolve', p }), setReason(''))}>
                        Uznaj za rozpatrzone
                      </Button>
                    </Group>
                  )}
                </Stack>
              )}
              <Text size="xs" c="dimmed" ff="monospace">
                SHA-256 treści: {p.contentHash.slice(0, 16)}…{p.contentHash.slice(-8)}
              </Text>
              {p.status === 'uniewazniony' ? (
                <Badge color="gray" size="sm" mt={4}>
                  unieważniony: {p.voidReason}
                </Badge>
              ) : (
                <Badge color={deliveryColor(p.delivery)} size="sm" mt={4}>
                  {PROTOCOL_DELIVERY_STATES[p.delivery]}
                </Badge>
              )}
              {p.mails.map((m) => (
                <Text key={m.id} size="xs" c="dimmed">
                  {m.kind === 'protokol_auto' ? 'automatycznie' : 'ręcznie'} → {m.toEmail}: {MAIL_STATUS[m.status]?.label ?? m.status}
                  {m.sentAt ? `, ${formatDateTime(m.sentAt)}` : ''}
                  {m.lastError ? ` (${m.lastError})` : ''}
                </Text>
              ))}
            </div>
            <Stack gap={4} align="flex-end">
              {p.pdfReady && (
                <Anchor href={`/api/protocols/${p.id}/pdf`} target="_blank" size="xs">
                  <Group gap={2}>
                    <IconFileTypePdf size={14} /> PDF
                  </Group>
                </Anchor>
              )}
              {can.resendProtocol && p.pdfReady && (
                <Button size="compact-xs" variant="subtle" onClick={() => (setDialog({ kind: 'resend', p }), setEmail(p.email ?? ''), setReason(''))}>
                  Wyślij ponownie
                </Button>
              )}
              {can.voidProtocol && p.status !== 'uniewazniony' && (
                <Button size="compact-xs" variant="subtle" color="red" onClick={() => (setDialog({ kind: 'void', p }), setReason(''))}>
                  Unieważnij
                </Button>
              )}
            </Stack>
          </Group>
        </Alert>
      ))}
      <Text size="xs" c="dimmed">
        {me?.environment.demo && 'W środowisku demonstracyjnym wiadomości trafiają do testowej skrzynki (Mailpit) — nie do klienta. '}
        „Wysłano” oznacza przyjęcie przez serwer poczty, nie doręczenie.
      </Text>
      <Modal opened={dialog?.kind === 'void' || dialog?.kind === 'resend'} onClose={() => setDialog(null)} title={dialog?.kind === 'void' ? `Unieważnienie ${dialog.p.code}` : `Ponowna wysyłka ${dialog?.p.code ?? ''}`}>
        <Stack>
          {dialog?.kind === 'void' && (
            <Text size="sm">
              Treść podpisanego protokołu się nie zmienia. Unieważnienie oznacza, że poprawka wymaga nowego protokołu z ponownym podpisem klienta (w aplikacji mobilnej).
            </Text>
          )}
          {dialog?.kind === 'resend' && <TextInput label="Adres e-mail" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />}
          <TextInput label="Powód" required description={reasonHint} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDialog(null)}>
              Anuluj
            </Button>
            <Button color={dialog?.kind === 'void' ? 'red' : undefined} loading={run.isPending} disabled={reason.trim().length < minReason} onClick={() => run.mutate()}>
              {dialog?.kind === 'void' ? 'Unieważnij' : 'Wyślij'}
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Modal opened={dialog?.kind === 'rework' || dialog?.kind === 'resolve'} onClose={() => setDialog(null)} title={dialog?.kind === 'rework' ? 'Zlecenie poprawki' : 'Uwagi rozpatrzone bez poprawki'}>
        <Stack>
          <Text size="sm">Uwagi klienta: „{dialog?.p.clientRemarks}”</Text>
          <Textarea
            label={dialog?.kind === 'rework' ? 'Co ekipa ma poprawić' : 'Jak rozpatrzono uwagi'}
            description={dialog?.kind === 'rework' ? 'Powstanie zadanie „Poprawka” dla zakresów protokołu (bez warunków startu). Po jego zakończeniu uwagi zamkną się same.' : 'np. uwaga informacyjna, uzgodniono z klientem — wpis zostaje w historii sprawy'}
            autosize
            minRows={3}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDialog(null)}>
              Anuluj
            </Button>
            <Button disabled={reason.trim().length < 3} loading={run.isPending} onClick={() => run.mutate()}>
              {dialog?.kind === 'rework' ? 'Utwórz poprawkę' : 'Zapisz'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
